import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Panel, Controls, MiniMap, useNodesState, useEdgesState, addEdge, getOutgoers, useReactFlow } from '@xyflow/react';
import { Grid, TextField, Button, Dialog, DialogTitle, DialogContent, Box, IconButton, Typography, Snackbar, Alert, Autocomplete, FormControlLabel, Checkbox } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import NodeComponent from './NodeComponent';
import EdgeComponent from './EdgeComponent';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../config";
import { WF_STATUS } from "./workflowConstants";

import '@xyflow/react/dist/style.css';

// Start/End only — task/decision/approval types are bound with onEditNode
// inside the builder (see nodeTypesWithHandlers)
const nodeTypes = {
    'inputNode': NodeComponent,
    'outputNode': NodeComponent
};

const initialNodes = [
    {
        id: '0',
        position: { x: 0, y: 0 },
        data: { label: 'Start' },
        type: 'inputNode',
        deletable: false
    },
    {
        id: '-1',
        position: { x: 500, y: 500 },
        data: { label: 'End' },
        type: 'outputNode',
        deletable: false
    },
];

// Next available node id = highest existing numeric id + 1. Using nodes.length
// breaks after a node is deleted and the workflow is saved (id collisions).
const getNextNodeId = (nodes) => nodes.reduce((maxId, node) => Math.max(maxId, parseInt(node.id, 10) || 0), 0) + 1;

const nodeColor = (node) => {
    switch (node.type) {
        case 'inputNode':
            return 'lightgreen';
        case 'outputNode':
            return '#ff4d4d';
        case 'decisionNode':
            return 'pink';
        case 'approvalNode':
            return 'plum';
        default:
            return 'lightblue';
    }
};

const NodeForm = ({ open, nodeLabel, editingNode, onSave, onClose }) => {
    const [nodeTitle, setNodeTitle] = useState(editingNode?.data.nodeTitle || '');
    const [nodeDescription, setNodeDescription] = useState(editingNode?.data.nodeDescription || 'Workflow Orchestrator Task');
    const [expectedStartDate, setExpectedStartDate] = useState(editingNode?.data.expectedStartDate ? moment(editingNode.data.expectedStartDate, 'MM-DD-YYYY') : null);
    const [expectedEndDate, setExpectedEndDate] = useState(editingNode?.data.expectedEndDate ? moment(editingNode.data.expectedEndDate, 'MM-DD-YYYY') : null);
    const [completeInDays, setCompleteInDays] = useState(editingNode?.data.completeInDays || '');
    const [signatureRequired, setSignatureRequired] = useState(!!editingNode?.data.signatureRequired);

    useEffect(() => {
        if (editingNode) {
            setNodeTitle(editingNode.data.nodeTitle || '');
            setNodeDescription(editingNode.data.nodeDescription || 'Workflow Orchestrator Task');
            setExpectedStartDate(editingNode.data.expectedStartDate ? moment(editingNode.data.expectedStartDate, 'MM-DD-YYYY') : null);
            setExpectedEndDate(editingNode.data.expectedEndDate ? moment(editingNode.data.expectedEndDate, 'MM-DD-YYYY') : null);
            setCompleteInDays(editingNode.data.completeInDays || '');
            setSignatureRequired(!!editingNode.data.signatureRequired);
        }
    }, [editingNode]);

    const handleDaysChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        setCompleteInDays(val);
        if (val) {
            setExpectedStartDate(null);
            setExpectedEndDate(null);
        }
    };

    const handleStartDateChange = (newValue) => {
        setExpectedStartDate(newValue);
        if (newValue) setCompleteInDays('');
    };

    const handleEndDateChange = (newValue) => {
        setExpectedEndDate(newValue);
        if (newValue) setCompleteInDays('');
    };

    const handleCloseNodeForm = () => {
        onClose();
        setNodeTitle('');
        setNodeDescription('Workflow Orchestrator Task');
        setExpectedStartDate(null);
        setExpectedEndDate(null);
        setCompleteInDays('');
        setSignatureRequired(false);
    };

    const canSave = !!nodeTitle && !!nodeDescription && (
        (expectedStartDate && expectedEndDate) ||
        (completeInDays && parseInt(completeInDays) > 0)
    );

    return (
        <div>
            <Dialog open={open} onClose={handleCloseNodeForm}>
                <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                    {editingNode ? `Edit ${nodeLabel} Node` : `Add ${nodeLabel} Node`}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Node Title"
                        value={nodeTitle}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setNodeTitle(event.target.value)}
                        fullWidth
                        error={!nodeTitle}
                    />
                    <TextField
                        label="Node Description"
                        value={nodeDescription}
                        sx={{ marginBottom: 2 }}
                        onChange={(event) => setNodeDescription(event.target.value)}
                        fullWidth
                        error={!nodeDescription}
                    />
                    <TextField
                        label='Complete before (days after workflow start)'
                        type='number'
                        value={completeInDays}
                        onChange={handleDaysChange}
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        inputProps={{ min: 1 }}
                        helperText="Leave blank to use explicit dates below"
                        FormHelperTextProps={{
                            sx: { color: '#1976D2' }
                        }}
                    />
                    <Grid>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Expected Start Date"
                                value={expectedStartDate}
                                onChange={handleStartDateChange}
                                disablePast
                                sx={{ width: '49%', marginRight: 1, marginBottom: 3 }}
                                disabled={!!completeInDays}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Expected End Date"
                                value={expectedEndDate}
                                onChange={handleEndDateChange}
                                disablePast
                                sx={{ width: '49%', marginBottom: 3 }}
                                disabled={!!completeInDays}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <FormControlLabel
                        control={<Checkbox checked={signatureRequired} onChange={(event) => setSignatureRequired(event.target.checked)} />}
                        label="Signature required at clock-out"
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ marginTop: 2 }}
                        onClick={() => {
                            onSave(
                                nodeTitle,
                                nodeDescription,
                                completeInDays ? null : expectedStartDate,
                                completeInDays ? null : expectedEndDate,
                                completeInDays ? parseInt(completeInDays) : null,
                                signatureRequired
                            );
                            handleCloseNodeForm();
                        }}
                        disabled={!canSave}
                    >
                        {editingNode ? 'Save Node' : 'Add Node'}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const DecisionNodeForm = ({ open, nodeLabel, editingNode, customerSchema, customerSchemaDefaults, projectSchema, projectSchemaDefaults, onSave, onClose }) => {
    const [nodeTitle, setNodeTitle] = useState(editingNode?.data.nodeTitle || '');
    const [nodeDescription, setNodeDescription] = useState(editingNode?.data.nodeDescription || 'Workflow Orchestrator Condition Node');
    const [attributeType, setAttributeType] = useState(editingNode?.data.attributeType || '');
    const [attributeName, setAttributeName] = useState(editingNode?.data.attributeName || '');
    const [operator, setOperator] = useState(editingNode?.data.operator || '');
    const [conditionValue, setConditionValue] = useState(editingNode?.data.conditionValue || '');

    const attributeOptions = attributeType === 'Customer'
        ? ((customerSchema && customerSchema.length) ? customerSchema : customerSchemaDefaults)
        : attributeType === 'Project'
            ? ((projectSchema && projectSchema.length) ? projectSchema : projectSchemaDefaults)
            : [];

    useEffect(() => {
        if (editingNode) {
            setNodeTitle(editingNode.data.nodeTitle || '');
            setNodeDescription(editingNode.data.nodeDescription || 'Workflow Orchestrator Condition Node');
            setAttributeType(editingNode.data.attributeType || '');
            setAttributeName(editingNode.data.attributeName || '');
            setOperator(editingNode.data.operator || '');
            setConditionValue(editingNode.data.conditionValue || '');
        }
    }, [editingNode]);

    const handleCloseDecisionNodeForm = () => {
        onClose();
        setNodeTitle('');
        setNodeDescription('Workflow Orchestrator Condition Node');
        setAttributeType('');
        setAttributeName('');
        setOperator('');
        setConditionValue('');
    };

    const canSave = !!nodeTitle && !!nodeDescription && !!attributeType && !!attributeName && !!operator && !!conditionValue;

    return (
        <div>
            <Dialog open={open} onClose={handleCloseDecisionNodeForm} fullWidth>
                <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                    {editingNode ? `Edit ${nodeLabel} Node` : `Add ${nodeLabel} Node`}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Node Title"
                        value={nodeTitle}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setNodeTitle(event.target.value)}
                        fullWidth
                        error={!nodeTitle}
                    />
                    <TextField
                        label="Node Description"
                        value={nodeDescription}
                        sx={{ marginBottom: 2 }}
                        onChange={(event) => setNodeDescription(event.target.value)}
                        fullWidth
                        error={!nodeDescription}
                    />
                    <Autocomplete
                        id="attr-type-select"
                        options={[{ title: 'Customer', id: 'Customer' }, { title: 'Project', id: 'Project' }]}
                        value={attributeType ? { title: attributeType, id: attributeType } : null}
                        getOptionLabel={(option) => option.title}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => { setAttributeType(value ? value.title : ''); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {option.title}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Attribute Type" error={!attributeType} />}
                        sx={{ marginBottom: 2 }}
                    />
                    <Grid container spacing={1}>
                        <Autocomplete
                            id="attr-type-select"
                            options={attributeOptions.map(attr => ({ title: attr.columnName, id: attr.columnName }))}
                            value={attributeName ? { title: attributeName, id: attributeName } : null}
                            getOptionLabel={(option) => option.title}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(event, value) => { setAttributeName(value ? value.title : ''); }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.title}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} label="Select Attribute" error={!attributeName} />}
                            sx={{ marginBottom: 3, width: '35%' }}
                        />
                        <Autocomplete
                            id="operator-select"
                            options={[{ title: 'is', id: 'is' }, { title: 'is not', id: 'is not' }, { title: 'greater than', id: 'greater than' }, { title: 'less than', id: 'less than' }]}
                            value={operator ? { title: operator, id: operator } : null}
                            getOptionLabel={(option) => option.title}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(event, value) => { setOperator(value ? value.title : ''); }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.title}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} label="Select Operator" error={!operator} />}
                            sx={{ marginBottom: 3, width: '27%' }}
                        />
                        <TextField
                            label="Value"
                            value={conditionValue}
                            sx={{ marginBottom: 3, width: '35%' }}
                            onChange={(event) => setConditionValue(event.target.value)}
                            fullWidth
                            error={!conditionValue}
                        />
                    </Grid>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ marginTop: 2 }}
                        onClick={() => {
                            onSave(
                                nodeTitle,
                                nodeDescription,
                                attributeType,
                                attributeName,
                                operator,
                                conditionValue
                            );
                            handleCloseDecisionNodeForm();
                        }}
                        disabled={!canSave}
                    >
                        {editingNode ? 'Save Condition Node' : 'Add Condition Node'}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const ApprovalNodeForm = ({ open, nodeLabel, editingNode, employeeList, onSave, onClose }) => {
    const [nodeTitle, setNodeTitle] = useState(editingNode?.data.nodeTitle || '');
    const [nodeDescription, setNodeDescription] = useState(editingNode?.data.nodeDescription || 'Workflow Orchestrator Approval Node');
    const [approverEmpId, setApproverEmpId] = useState(editingNode?.data.approverEmpId || '');

    useEffect(() => {
        if (editingNode) {
            setNodeTitle(editingNode.data.nodeTitle || '');
            setNodeDescription(editingNode.data.nodeDescription || 'Workflow Orchestrator Approval Node');
            setApproverEmpId(editingNode.data.approverEmpId || '');
        }
    }, [editingNode]);

    const handleCloseApprovalNodeForm = () => {
        onClose();
        setNodeTitle('');
        setNodeDescription('Workflow Orchestrator Approval Node');
        setApproverEmpId('');
    };

    const selectedApprover = employeeList.find((emp) => emp.emp_id === approverEmpId) || null;

    const canSave = !!nodeTitle && !!nodeDescription && !!approverEmpId;

    return (
        <div>
            <Dialog open={open} onClose={handleCloseApprovalNodeForm} fullWidth>
                <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                    {editingNode ? `Edit ${nodeLabel} Node` : `Add ${nodeLabel} Node`}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ marginTop: 2, marginBottom: 2 }}>
                        This node will be used to approve or reject the forms associated with the previous task node.
                    </Alert>
                    <TextField
                        label="Node Title"
                        value={nodeTitle}
                        sx={{ marginBottom: 2 }}
                        onChange={(event) => setNodeTitle(event.target.value)}
                        fullWidth
                        error={!nodeTitle}
                    />
                    <TextField
                        label="Node Description"
                        value={nodeDescription}
                        sx={{ marginBottom: 2 }}
                        onChange={(event) => setNodeDescription(event.target.value)}
                        fullWidth
                        error={!nodeDescription}
                    />
                    <Autocomplete
                        id="approver-select"
                        options={employeeList}
                        value={selectedApprover}
                        getOptionLabel={(option) => option.emp_name}
                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                        onChange={(event, value) => { setApproverEmpId(value ? value.emp_id : ''); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Approver" error={!approverEmpId} />}
                        sx={{ marginBottom: 2 }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ marginTop: 2 }}
                        onClick={() => {
                            onSave(nodeTitle, nodeDescription, approverEmpId, selectedApprover?.emp_name || '');
                            handleCloseApprovalNodeForm();
                        }}
                        disabled={!canSave}
                    >
                        {editingNode ? 'Save Approval Node' : 'Add Approval Node'}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const ReactFlowComponent = (props) => {
    const [workflowTitles, setWorkflowTitles] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [nodeId, setNodeId] = useState(props.editMode ? getNextNodeId(props.nodes) : 1);
    const [nodesAfterSave, setNodesAfterSave] = useState([]);
    const [edgesAfterSave, setEdgesAfterSave] = useState([]);
    const [WfFormOpen, setWfFormOpen] = useState(false);
    const [WfVersion, setWfVersion] = useState(props.editMode ? (parseInt(props.wf_version) + 1).toString() : '1');
    const [WfTitle, setWfTitle] = useState(props.editMode ? props.wf_title : '');
    const [WfDescription, setWfDescription] = useState(props.editMode ? props.wf_description : '');
    const [WfStatus, setWfStatus] = useState(props.editMode ? props.wf_status : WF_STATUS.DRAFT);
    const [nodeFormOpen, setNodeFormOpen] = useState(false);
    const [customerSchemaLocal, setCustomerSchemaLocal] = useState([]);
    const [customerSchemaDefaultsLocal, setCustomerSchemaDefaultsLocal] = useState([]);
    const [projectSchemaLocal, setProjectSchemaLocal] = useState([]);
    const [projectSchemaDefaultsLocal, setProjectSchemaDefaultsLocal] = useState([]);
    const [decisionNodeFormOpen, setDecisionNodeFormOpen] = useState(false);
    const [approvalNodeFormOpen, setApprovalNodeFormOpen] = useState(false);
    const [nodeLabel, setNodeLabel] = useState(null);
    const [editNodeId, setEditNodeId] = useState(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [savingWf, setSavingWf] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [intakeFormAvailable, setIntakeFormAvailable] = useState(false);
    const [employeeList, setEmployeeList] = useState([]);

    const reactFlowWrapper = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        // Independent requests — run them in parallel instead of as a waterfall
        await Promise.all([
            axios
                .get(APIURL + "/dropdowns/dropdownType/taskTypeList", {
                    params: { comp_id: props.comp_id },
                }).then((response) => {
                    setTaskTypes(response.data.filter((taskType) => [0, 3].includes(taskType.is_Active)));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/workflows", {
                    params: { compID: props.comp_id },
                }).then((response) => {
                    setWorkflowTitles(props.editMode ? response.data.filter((wf) => wf.wf_id !== props.wf_id).map((wf) => wf.wf_title) : response.data.map((wf) => wf.wf_title));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/schema/table/customer", {
                    params: { companyID: props.comp_id },
                }).then((response) => {
                    setCustomerSchemaLocal(response.data.filter(col => col.columnVisible));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/schema/table-default/customer", {
                    params: {
                        companyState: props.user.comp_settings.find(s => s.feature_id === 99)?.state_id || null,
                        companyTypeOfBusiness: props.user.comp_settings.find(s => s.feature_value === 'primaryBusinessType')?.feature_id || null
                    },
                }).then((response) => {
                    setCustomerSchemaDefaultsLocal(response.data.filter(col => col.columnVisible));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/schema/table/project", {
                    params: { companyID: props.comp_id },
                }).then((response) => {
                    setProjectSchemaLocal(response.data.filter(col => col.columnVisible));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/schema/table-default/project", {
                    params: {
                        companyState: props.user.comp_settings.find(s => s.feature_id === 99)?.state_id || null,
                        companyTypeOfBusiness: props.user.comp_settings.find(s => s.feature_value === 'primaryBusinessType')?.feature_id || null
                    },
                }).then((response) => {
                    setProjectSchemaDefaultsLocal(response.data.filter(col => col.columnVisible));
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/dropdowns/dropdownType/fullTaskType", {
                    params: { comp_id: props.comp_id },
                }).then(async (response) => {
                    const intakeTaskType = response.data.filter((type) => type.EntityValue === 'Intake')?.[0]?.ddID || null;

                    if (intakeTaskType) {
                        await axios
                            .get(APIURL + "/task_form", {
                                params: { taskType_id: intakeTaskType, company_id: props.comp_id },
                            }).then((response) => {
                                setIntakeFormAvailable(response.data.length > 0);
                            }).catch((error) => {
                                console.log(error);
                            });
                    }
                }).catch((error) => {
                    console.log(error);
                }),

            axios
                .get(APIURL + "/dropdowns/dropdownType/employeeslist", {
                    params: { comp_id: props.comp_id },
                }).then((response) => {
                    setEmployeeList(response.data.map((emp) => ({ emp_id: emp.id, emp_name: emp.employee_name })));
                }).catch((error) => {
                    console.log(error);
                }),
        ]);
    };

    const [nodes, setNodes, onNodesChange] = useNodesState(props.editMode ? props.nodes : initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(props.editMode ? props.edges : []);

    const { getNodes, getEdges, screenToFlowPosition } = useReactFlow();

    // Place new nodes at the center of the current viewport, with a small
    // per-node offset so consecutive adds don't stack on top of each other
    const getNewNodePosition = () => {
        const bounds = reactFlowWrapper.current?.getBoundingClientRect();
        if (!bounds) return { x: nodeId * 25, y: nodeId * 50 };
        const offset = (nodeId % 5) * 20;
        return screenToFlowPosition({
            x: bounds.x + bounds.width / 2 + offset,
            y: bounds.y + bounds.height / 2 + offset,
        });
    };

    const onConnect = useCallback(
        (connection) => setEdges((eds) => addEdge({ ...connection, type: 'actionEdge', animated: true, data: { action: null } }, eds)),
        [setEdges],
    );

    const handleNode = (nodeTitle, nodeDescription, expectedStartDate, expectedEndDate, completeInDays = null, signatureRequired = false) => {
        if (editNodeId !== null) {
            // Edit existing node
            setNodes((prevNodes) =>
                prevNodes.map((node) =>
                    node.id === editNodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                nodeTitle,
                                nodeDescription,
                                expectedStartDate: expectedStartDate ? moment(expectedStartDate).format('MM-DD-YYYY') : null,
                                expectedEndDate: expectedEndDate ? moment(expectedEndDate).format('MM-DD-YYYY') : null,
                                completeInDays: completeInDays || '',
                                signatureRequired,
                            },
                        }
                        : node
                )
            );
            setEditNodeId(null);
        } else {
            // Add new node
            const newNode = {
                id: nodeId.toString(),
                position: getNewNodePosition(),
                data: {
                    label: nodeLabel,
                    nodeTitle,
                    nodeDescription,
                    expectedStartDate: expectedStartDate ? moment(expectedStartDate).format('MM-DD-YYYY') : null,
                    expectedEndDate: expectedEndDate ? moment(expectedEndDate).format('MM-DD-YYYY') : null,
                    completeInDays: completeInDays || '',
                    signatureRequired,
                },
                type: 'taskTypeNode'
            };
            setNodes((prevNodes) => [...prevNodes, newNode]);
            setNodeId((prevId) => prevId + 1);
        }
    };

    const handleDecisionNode = (nodeTitle, nodeDescription, attributeType, attributeName, operator, conditionValue) => {
        if (editNodeId !== null) {
            // Edit existing node
            setNodes((prevNodes) =>
                prevNodes.map((node) =>
                    node.id === editNodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                nodeTitle,
                                nodeDescription,
                                attributeType,
                                attributeName,
                                operator,
                                conditionValue
                            },
                        }
                        : node
                )
            );
            setEditNodeId(null);
        } else {
            // Add new node
            const newNode = {
                id: nodeId.toString(),
                position: getNewNodePosition(),
                data: {
                    label: nodeLabel,
                    nodeTitle,
                    nodeDescription,
                    attributeType,
                    attributeName,
                    operator,
                    conditionValue
                },
                type: 'decisionNode'
            };
            setNodes((prevNodes) => [...prevNodes, newNode]);
            setNodeId((prevId) => prevId + 1);
        }
    };

    const handleApprovalNode = (nodeTitle, nodeDescription, approverEmpId, approverEmpName) => {
        if (editNodeId !== null) {
            // Edit existing node
            setNodes((prevNodes) =>
                prevNodes.map((node) =>
                    node.id === editNodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                nodeTitle,
                                nodeDescription,
                                approverEmpId,
                                approverEmpName
                            },
                        }
                        : node
                )
            );
            setEditNodeId(null);
        } else {
            // Add new node
            const newNode = {
                id: nodeId.toString(),
                position: getNewNodePosition(),
                data: {
                    label: nodeLabel,
                    nodeTitle,
                    nodeDescription,
                    approverEmpId,
                    approverEmpName
                },
                type: 'approvalNode'
            };
            setNodes((prevNodes) => [...prevNodes, newNode]);
            setNodeId((prevId) => prevId + 1);
        }
    };

    const handleEditNode = useCallback((targetNodeId) => {
        const node = getNodes().find(n => n.id === targetNodeId);
        if (node) {
            setNodeLabel(node.data.label);
            setEditNodeId(targetNodeId);
            if (node.type === 'decisionNode') {
                setDecisionNodeFormOpen(true);
            } else if (node.type === 'approvalNode') {
                setApprovalNodeFormOpen(true);
            } else {
                setNodeFormOpen(true);
            }
        }
    }, [getNodes]);

    // nodeTypes/edgeTypes must be referentially stable — recreating them inline on
    // every render makes React Flow remount all nodes (and logs a warning)
    const nodeTypesWithHandlers = useMemo(() => ({
        ...nodeTypes,
        taskTypeNode: (nodeProps) => (
            <NodeComponent {...nodeProps} onEditNode={handleEditNode} />
        ),
        decisionNode: (nodeProps) => (
            <NodeComponent {...nodeProps} onEditNode={handleEditNode} />
        ),
        approvalNode: (nodeProps) => (
            <NodeComponent {...nodeProps} onEditNode={handleEditNode} />
        )
    }), [handleEditNode]);

    const edgeTypesWithProps = useMemo(() => ({
        actionEdge: (edgeProps) => (
            <EdgeComponent {...edgeProps} intakeFormAvailable={intakeFormAvailable} />
        )
    }), [intakeFormAvailable]);

    const editingNode = editNodeId !== null ? nodes.find(n => n.id === editNodeId) : null;

    const handleNodeFormClose = () => {
        setNodeFormOpen(false);
        setDecisionNodeFormOpen(false);
        setApprovalNodeFormOpen(false);
        setNodeLabel(null);
        setEditNodeId(null);
    };

    const isValidConnection = useCallback(
        (connection) => {
            const nodes = getNodes();
            const edges = getEdges();
            const target = nodes.find((node) => node.id === connection.target);
            const source = nodes.find((node) => node.id === connection.source);

            // An Approval node can only be placed directly after a task type node.
            if (target.type === 'approvalNode' && source?.type !== 'taskTypeNode') return false;

            const hasCycle = (node, visited = new Set()) => {
                if (visited.has(node.id)) return false;

                visited.add(node.id);

                for (const outgoer of getOutgoers(node, nodes, edges)) {
                    if (outgoer.id === connection.source) return true;
                    if (hasCycle(outgoer, visited)) return true;
                }
            };

            if (target.id === connection.source) return false;
            return !hasCycle(target);
        },
        [getNodes, getEdges],
    );

    const confirmationDialog = () => {
        const handleConfirm = () => {
            setConfirmationOpen(false);
            handleSave();
        };

        const handleCancel = () => {
            setConfirmationOpen(false);
        };

        return (
            <div>
                <Dialog open={confirmationOpen} onClose={handleCancel}>
                    <DialogTitle>Confirm Action</DialogTitle>
                    <DialogContent>
                        <div>Editing a Published workflow would affect all active workflow instances. Are you sure you want to continue?</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button onClick={handleConfirm} variant="contained" color="primary">Yes, Continue</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    };

    const WorkflowForm = () => {
        return (
            <div>
                <Dialog open={WfFormOpen} onClose={alertMessage ? handleSnackbarAlertClose : handleCloseWfForm}>
                    <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                        {props.editMode ? 'Edit Workflow' : 'Create Workflow'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Workflow Title"
                            value={WfTitle}
                            sx={{ marginTop: 2, marginBottom: 2 }}
                            onChange={(event) => setWfTitle(event.target.value)}
                            size="small"
                            fullWidth
                            error={!WfTitle}
                            helperText={workflowTitles.includes(WfTitle) ? 'A workflow with this title already exists.' : ''}
                            FormHelperTextProps={{
                                sx: { color: '#1976D2' }
                            }}
                        />
                        <TextField
                            label="Workflow Description"
                            value={WfDescription}
                            sx={{ marginBottom: 2 }}
                            onChange={(event) => setWfDescription(event.target.value)}
                            size="small"
                            fullWidth
                            error={!WfDescription}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ marginTop: 2 }}
                            onClick={() => { WfStatus === WF_STATUS.PUBLISHED ? setConfirmationOpen(true) : handleSave() }}
                            disabled={savingWf || !WfTitle || !WfDescription}
                        >
                            Save Workflow
                        </Button>
                    </DialogContent>
                </Dialog>
            </div>
        );
    };

    const handleCloseWfForm = () => {
        setWfFormOpen(false);
    };

    const handleSave = async () => {
        setSavingWf(true);

        if (props.editMode) {
            await axios
                .put(APIURL + `/workflows`, {
                    wf_id: props.wf_id,
                    wf_version: WfVersion,
                    wf_title: WfTitle,
                    wf_description: WfDescription,
                    wf_nodes: JSON.stringify(nodes),
                    wf_edges: JSON.stringify(edges),
                    wf_status: WfStatus,
                    wf_createdBy: props.user.emp_id,
                })
                .then(function (response) {
                    setAlertMessage('Workflow saved successfully!');
                    setAlertType('success');
                })
                .catch(function (error) {
                    console.log(error);
                    setAlertMessage('An error occurred while saving the workflow!');
                    setAlertType('error');
                });
        } else {
            await axios
                .post(APIURL + `/workflows`, {
                    wf_version: WfVersion,
                    wf_title: WfTitle,
                    wf_description: WfDescription,
                    wf_nodes: JSON.stringify(nodes),
                    wf_edges: JSON.stringify(edges),
                    wf_status: WfStatus,
                    wf_companyID: props.comp_id,
                    wf_createdBy: props.user.emp_id,
                })
                .then(function (response) {
                    setNodesAfterSave(nodes);
                    setEdgesAfterSave(edges);
                    setAlertMessage('Workflow saved successfully!');
                    setAlertType('success');
                })
                .catch(function (error) {
                    console.log(error);
                    setAlertMessage('An error occurred while saving the workflow!');
                    setAlertType('error');
                });
        }
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');

        if (props.editMode) {
            props.setEditMode(false);
            props.fetchData();
        } else {
            fetchData();
        }

        handleCloseWfForm();
        setSavingWf(false);
    }

    const nodePickerOptions = [
        { label: 'Condition', group: 'Logic', kind: 'decision' },
        { label: 'Approval', group: 'Logic', kind: 'approval' },
        ...taskTypes.map((type) => ({ label: type.EntityValue, group: 'Action', kind: 'task' })),
    ];

    const nodePickerGlyphStyles = {
        'decision': { width: 10, height: 10, background: 'pink', transform: 'rotate(45deg)', flexShrink: 0, marginLeft: 0.5, marginRight: 1.5 },
        'approval': { width: 10, height: 10, background: 'plum', transform: 'rotate(45deg)', flexShrink: 0, marginLeft: 0.5, marginRight: 1.5 },
        'task': { width: 16, height: 10, background: 'lightblue', borderRadius: '2px', flexShrink: 0, marginRight: 1 },
    };

    const NodePickerComponent = () => {
        return (
            <Autocomplete
                id="add-node-select"
                size="small"
                options={nodePickerOptions}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.label}
                value={null}
                blurOnSelect
                clearOnBlur
                onChange={(event, option) => {
                    if (!option) return;
                    setNodeLabel(option.label);
                    if (option.kind === 'decision') {
                        setDecisionNodeFormOpen(true);
                    } else if (option.kind === 'approval') {
                        setApprovalNodeFormOpen(true);
                    } else {
                        setNodeFormOpen(true);
                    }
                }}
                renderOption={(props, option) => (
                    <li {...props} key={`${option.group}-${option.label}`}>
                        <Box sx={nodePickerGlyphStyles[option.kind]} />
                        <Typography variant="body2">{option.label}</Typography>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Add a node"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <AddIcon fontSize="small" sx={{ color: 'gray' }} />
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                sx={{ width: 230, background: 'white', borderRadius: 1, boxShadow: 1 }}
            />
        );
    };

    const RightPanelComponent = () => {
        return (
            <div>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<ReplayIcon />}
                    sx={{ background: 'white', color: 'black', '&:hover': { background: '#F0F0F0' } }}
                    onClick={props.editMode ? () => { setNodes(props.nodes); setEdges(props.edges); setNodeId(getNextNodeId(props.nodes)); } : () => { setNodes(initialNodes); setEdges([]); setNodeId(1); }}
                >
                    Discard
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    sx={{ marginLeft: 1 }}
                    onClick={() => setWfFormOpen(true)}
                    disabled={props.editMode
                        ? (props.nodes === nodes && props.edges === edges)
                        : (nodes.length < 3 || edges.length < 1 || (nodes === nodesAfterSave && edges === edgesAfterSave))
                    }
                >
                    Save Workflow
                </Button>
            </div>
        );
    };

    return (
        <div ref={reactFlowWrapper} style={{ width: '100%', height: props.editMode ? 'calc(100vh - 185px)' : 'calc(100vh - 135px)' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                nodeTypes={nodeTypesWithHandlers}
                edgeTypes={edgeTypesWithProps}
                fitView
                elementsSelectable={false}
            >
                <Panel position="top-left">{NodePickerComponent()}</Panel>
                <Panel position="top-right">{RightPanelComponent()}</Panel>
                <Controls position="bottom-right" style={{ marginBottom: 175 }} />
                <MiniMap nodeColor={nodeColor} zoomable pannable />
                <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
            <NodeForm
                open={nodeFormOpen}
                nodeLabel={nodeLabel}
                editingNode={editingNode}
                onSave={handleNode}
                onClose={handleNodeFormClose}
            />
            <DecisionNodeForm
                open={decisionNodeFormOpen}
                nodeLabel={nodeLabel}
                editingNode={editingNode}
                customerSchema={customerSchemaLocal}
                customerSchemaDefaults={customerSchemaDefaultsLocal}
                projectSchema={projectSchemaLocal}
                projectSchemaDefaults={projectSchemaDefaultsLocal}
                onSave={handleDecisionNode}
                onClose={handleNodeFormClose}
            />
            <ApprovalNodeForm
                open={approvalNodeFormOpen}
                nodeLabel={nodeLabel}
                editingNode={editingNode}
                employeeList={employeeList}
                onSave={handleApprovalNode}
                onClose={handleNodeFormClose}
            />
            {confirmationDialog()}
            {WorkflowForm()}
            {SnackbarComponent()}
        </div>
    );
}

export default function WorkflowBuilder(props) {
    return (
        <ReactFlowProvider>
            <ReactFlowComponent {...props} />
        </ReactFlowProvider>
    );
}